import React, { useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../hooks/useWallet';
import { ADDR, NET } from '../config';
import { contract, fmt, getLogs, loadShips, chainNow, tierLabel, shipImg, reasonText, KIND, TIER } from '../lib';
import SeasonPanel from '../components/SeasonPanel';
import ShipCard from '../components/ShipCard';
import Info from '../components/Info';
import Gen1Panel from '../components/Gen1Panel';
import Reveal from '../components/Reveal';
import { short } from '../components/SeasonPanel';

// Dashboard v2: one obvious action per ship, everything else behind ⋯ or an ⓘ tooltip. Every action reports a plain-English
// result in the status bar (no silent returns). Approvals happen automatically as "Step 1 of 2".

const dur = (sec) => {
  if (sec <= 0) return 'now';
  const h = Math.floor(sec / 3600); const m = Math.ceil((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
};

function Modal({ title, onClose, children, testid }) {
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="cm-overlay" data-testid={testid} onClick={onClose}>
      <div className="cm-box" onClick={(e) => e.stopPropagation()}>
        <div className="cm-head"><h3>{title}</h3><button className="cm-x" aria-label="Close" onClick={onClose}>✕</button></div>
        {children}
      </div>
    </div>
  );
}

const MAX_CLAIM = 20; // Fleet.MAX_CLAIM_SHIPS: bigger collects are split into batches
async function readReq(f, hub, id, total) {
  const r = await f.getRequest(id);
  return { id, total, route: Number(r.route), unstake: r.unstake, settled: r.settled, n: r.ids.length, at: Number(r.requestedAt),
    word: r.settled || (await hub.randomOf(r.randId)) !== 0n };
}

function Dashboard() {
  const { account, send, run, nonce, connect, ore, setStatus, refresh } = useWallet();
  const [ships, setShips] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [gen1, setGen1] = useState({ n: 1 });
  const [g1pend, setG1pend] = useState([]);
  const [stats, setStats] = useState(null);
  const [now, setNow] = useState(Date.now() / 1000);
  const [mined, setMined] = useState(null);
  const [g1tick, setG1tick] = useState(0);
  const [rtick, setRtick] = useState(0);
  const [collect, setCollect] = useState(null); // { title, miners:[ids], pirates:[ids], unstake }
  const [route, setRoute] = useState(0);
  const [sell, setSell] = useState(null); // { ship, price }
  const [usdPerBnb, setUsdPerBnb] = useState(null);
  const [busy, setBusy] = useState(false);
  const [g1d, setG1d] = useState(null); // Gen1 panel chain data
  const [rateNow, setRateNow] = useState(null); // ORE/day for all miners right now
  const [reveal, setReveal] = useState(null);
  const pendRef = useRef(null);

  useEffect(() => {
    const f = contract('Fleet'); const y = contract('Shipyard');
    chainNow().then(setNow).catch(() => {});
    Promise.all([f.totalPower(), f.totalRankWeight(), f.minStake(), f.totalPaidMiners(), f.totalFuelBurned(), y.gen1Start(), y.gen1Requested(), f.rules(), y.gen1Delay(), y.hijackBps(), f.retryDelay(), y.upgradeBaseCost(), y.maxLevel(), y.repairCostPerPoint(), contract('Ships').maxDurability()])
      .then(([tp, rw, ms, paid, fuel, g1s, g1r, ru, g1d, hj, rd, upBase, maxLv, cpp, maxD]) => setStats({ tp, rw, ms: Number(ms), rd: Number(rd), paid, fuel, g1s: Number(g1s), g1r: Number(g1r), g1d: Number(g1d),
        fuelBps: Number(ru.fuelBps), fuelMin: ru.fuelMin, safeTaxBps: Number(ru.safeTaxBps), upBase, maxLv: Number(maxLv), cpp, maxD: Number(maxD), safeTax: Number(ru.safeTaxBps) / 100, riskyTax: Number(ru.riskyTaxBps) / 100, intercept: Number(ru.interceptBps) / 100, hijack: Number(hj) / 100 }))
      .catch(() => {});
    contract('Genesis').usdToWei(10n ** 18n).then((w) => setUsdPerBnb(1e18 / Number(w))).catch(() => {});
    if (!account) return;
    setShips(null);
    loadShips(account).then(setShips).catch(() => { setShips([]); setStatus({ kind: 'err', text: 'Could not load your ships: the network is busy. Refresh the page in a moment.' }); });
    const hub = contract('RandomnessHub');
    (async () => {
      const evs = await getLogs(f, f.filters.ReturnRequested(null, account));
      setReqs((await Promise.all(evs.map((ev) => readReq(f, hub, ev.args[0], ev.args[4])))).reverse());
      const mine = new Set(evs.map((ev) => String(ev.args[0])));
      const [rets, pcs] = await Promise.all([getLogs(f, f.filters.ShipReturned()), getLogs(f, f.filters.PirateClaimed(account))]);
      setMined(rets.filter((ev) => mine.has(String(ev.args[0]))).reduce((t, ev) => t + ev.args[2], 0n) + pcs.reduce((t, ev) => t + ev.args[2], 0n));
    })().catch(() => {});
  }, [account, nonce, setStatus]);

  // Claims settle by themselves when the dice roll arrives (v8.4). Poll open ones; refresh balances when one lands.
  useEffect(() => {
    if (!account || !reqs.some((r) => !r.settled)) return undefined;
    const t = setTimeout(async () => {
      const f = contract('Fleet'); const hub = contract('RandomnessHub');
      try {
        const fresh = await Promise.all(reqs.map((r) => (r.settled ? r : readReq(f, hub, r.id, r.total))));
        if (fresh.some((r, i) => r.settled && !reqs[i].settled)) refresh(); // ORE arrived: reload wallet + fleet
        else setReqs(fresh);
      } catch (e) { /* network busy: try again next tick */ }
      setRtick((x) => x + 1);
    }, 4000);
    return () => clearTimeout(t);
  }, [account, reqs, rtick, refresh]);

  // Gen1 builds (separate so polling does not reload the fleet).
  useEffect(() => {
    if (!account) return;
    const y = contract('Shipyard');
    (async () => {
      const gevs = await getLogs(y, y.filters.Gen1Requested(null, account));
      const rows = (await Promise.all(gevs.map(async (ev) => {
        const [p, st] = await Promise.all([y.pending(ev.args[0]), y.mintStatus(ev.args[0])]);
        return { pid: ev.args[0], count: Number(p.count), status: Number(st) }; // 1 drawing, 2 drawn, 3 delivered (automatic)
      }))).reverse();
      const prev = pendRef.current; pendRef.current = rows;
      if (prev) {
        const was = new Set(prev.filter((p) => p.status !== 3).map((p) => String(p.pid)));
        const done = rows.filter((p) => p.status === 3 && was.has(String(p.pid)));
        if (done.length) { loadShips(account).then(setShips).catch(() => {}); done.forEach((p) => openGen1Reveal(p.pid).catch(() => {})); }
      }
      setG1pend(rows);
    })().catch(() => {});
  }, [account, nonce, g1tick]);

  useEffect(() => {
    if (!g1pend.some((p) => p.status !== 3)) return undefined;
    const t = setTimeout(() => setG1tick((x) => x + 1), 5000);
    return () => clearTimeout(t);
  }, [g1pend]);

  // Gen1 panel: cap, odds, powers and price window straight from Shipyard / Fleet / OrePriceOracle; today's ORE/day rate.
  useEffect(() => {
    const y = contract('Shipyard'); const f = contract('Fleet'); const r = contract('EmissionReserve');
    (async () => {
      const cn = await chainNow(); const day = Math.floor(cn / 86400);
      const [start, delay, cap, today, perTx, pb, hb, mw, pw, pows, win, left, hasId] = await Promise.all([
        y.gen1Start(), y.gen1Delay(), y.dailyCap(), y.mintedOnDay(day), y.maxPerTx(), y.pirateBps(), y.hijackBps(),
        Promise.all([0, 1, 2].map((i) => y.minerTierWeights(i))), Promise.all([0, 1, 2].map((i) => y.pirateTierWeights(i))),
        Promise.all([0, 1, 2].map((i) => f.powerOf(i, 1))), contract('OrePriceOracle').window().catch(() => 1800n),
        account ? y.remaining(account) : Promise.resolve(0n), account ? y.hasAgentId(account) : Promise.resolve(false)]);
      setG1d((o) => ({ ...(o || {}), start: Number(start), delay: Number(delay), cap: Number(cap), today: Number(today), perTx: Number(perTx), pirateBps: Number(pb), hijackBps: Number(hb),
        minerW: mw.map(Number), pirateW: pw.map(Number), powers: pows.map(Number), window: Number(win), left: Number(left), hasId }));
      const p = await r.liveParams();
      if (Number(p.start) === 0) { setRateNow(0n); return; }
      const idx = Math.floor((cn - Number(p.start)) / Number(p.seasonLength));
      setRateNow(idx >= Number(p.maxSeasons) ? 0n : await r.rateAt(idx));
    })().catch(() => {});
  }, [account, nonce, g1tick]);

  // A build of this session just got delivered: open the same reveal as the Mint page with the ships that reached this wallet.
  const openGen1Reveal = async (pid) => {
    const y = contract('Shipyard'); const sh = contract('Ships');
    const evs = await getLogs(y, y.filters.Gen1Born(pid));
    const mine = evs.filter((e) => String(e.args[2]).toLowerCase() === String(account).toLowerCase());
    const lost = evs.length - mine.length;
    if (lost > 0) setStatus({ kind: 'err', text: `${lost} of your new ship${lost > 1 ? 's were' : ' was'} hijacked by a raiding pirate.` });
    if (!mine.length) return;
    const ships = await Promise.all(mine.map(async (e) => { const s = await sh.getShip(e.args[1]); return { id: e.args[1], kind: KIND[Number(s.kind)], tier: TIER[Number(s.tier)], serial: Number(s.serial) }; }));
    setReveal({ ships, title: `Gen1 build · ${ships.length} ship${ships.length > 1 ? 's' : ''}`, share: null });
  };

  useEffect(() => {
    if (!stats) return;
    contract('Shipyard').quoteFull(gen1.n).then((q) => setGen1((g) => ({ ...g, ok: q[0], why: q[1], cost: q[2], usd: q[3] }))).catch(() => setGen1((g) => ({ ...g, ok: false, why: 'NetworkBusy' })));
  }, [stats, gen1.n]);

  useEffect(() => { const t = setInterval(() => setNow((n) => n + 1), 1000); return () => clearInterval(t); }, []);

  if (!account) return (<div className="dashboard-container"><h1>Your Fleet</h1><button className="mint-btn" onClick={connect}>Connect Wallet</button></div>);

  const list = ships || [];
  const minStake = stats?.ms ?? 86400;
  const readyIn = (sh) => Number(sh.stake.since) + minStake - now; // seconds until a miner can be collected / brought home
  const locked = (sh) => sh.stake && sh.stake.req !== 0n;
  const idleShips = list.filter((s) => s.where === 'wallet');
  const readyMiners = list.filter((s) => s.where === 'staked' && s.kind === 'Miner' && !locked(s) && readyIn(s) <= 0);
  const stakedPirates = list.filter((s) => s.where === 'staked' && s.kind === 'Pirate');
  const hasMiners = list.some((s) => s.kind === 'Miner'); const hasPirates = list.some((s) => s.kind === 'Pirate');
  const job = (sh) => (sh.kind === 'Pirate' ? 'raid' : 'mine');

  // Wrap a whole user action: one busy flag, every failure shown in plain words.
  const act = (label, fn) => async () => {
    if (busy) return;
    setBusy(true);
    try { await run(label, fn); } finally { setBusy(false); }
  };
  // Approval is an automatic first step. Returns false if the user cancelled / it failed (status already explains why).
  const allowShips = async (op, total) => {
    if (await contract('Ships').isApprovedForAll(account, op)) return true;
    return !!(await send(`Step 1 of ${total}: allow the game to use your ships`, (s) => contract('Ships', s).setApprovalForAll(op, true)));
  };
  const allowOre = async (amt, total) => {
    if ((await contract('ORE').allowance(account, ADDR.Shipyard)) >= amt) return true;
    return !!(await send(`Step 1 of ${total}: allow the shipyard to use your ORE`, (s) => contract('ORE', s).approve(ADDR.Shipyard, ethers.MaxUint256)));
  };
  const checkOre = (need) => { if (ore < need) throw new Error(`You need ${fmt(need, 0)} ORE but have ${fmt(ore, 0)} ORE.`); };

  const sendToWork = (ships_, label) => act(label, async () => {
    if (!ships_.length) throw new Error('No ships in your hangar to send.');
    const approved = await contract('Ships').isApprovedForAll(account, ADDR.Fleet);
    if (!approved && !(await allowShips(ADDR.Fleet, 2))) return;
    await send(approved ? label : `Step 2 of 2: ${label.toLowerCase()}`, (s) => contract('Fleet', s).stake(ships_.map((x) => x.id)));
  });
  const doCollect = act('Collect', async () => {
    const c = collect; setCollect(null);
    const steps = Math.ceil(c.miners.length / MAX_CLAIM) + (c.pirates.length ? 1 : 0);
    let k = 0; const pre = () => (steps > 1 ? `Step ${++k} of ${steps}: ` : '');
    if (c.miners.length) {
      const verb = c.unstake ? 'bring home' : 'collect ORE from';
      const parts = []; for (let i = 0; i < c.miners.length; i += MAX_CLAIM) parts.push(c.miners.slice(i, i + MAX_CLAIM));
      for (let j = 0; j < parts.length; j++) {
        const batch = parts.length > 1 ? ` (batch ${j + 1} of ${parts.length})` : '';
        const ok = await send(`${pre()}${verb} ${parts[j].length} miner${parts[j].length > 1 ? 's' : ''} (${route ? 'gamble' : 'safe'})${batch}`, (s) => contract('Fleet', s).requestReturn(parts[j], route, c.unstake));
        if (!ok) return;
      }
    }
    if (c.pirates.length) await send(`${pre()}${c.unstake ? 'bring home' : 'collect loot from'} ${c.pirates.length} pirate${c.pirates.length > 1 ? 's' : ''}`, (s) => contract('Fleet', s).claimPirates(c.pirates, c.unstake));
  });
  const openCollect = (title, miners, pirates, unstake) => {
    if (!miners.length && !pirates.length) { setStatus({ kind: 'err', text: `${title}: nothing to collect yet. Miners can be collected ${dur(minStake)} after they start mining.` }); return; }
    setRoute(0);
    if (!miners.length) { // pirates only: no safe/gamble choice needed
      act(title, async () => { await send(`${unstake ? 'Bring home' : 'Collect loot from'} ${pirates.length} pirate${pirates.length > 1 ? 's' : ''}`, (s) => contract('Fleet', s).claimPirates(pirates, unstake)); })();
      return;
    }
    setCollect({ title, miners, pirates, unstake });
  };
  // Fallbacks only, for a claim that did not settle by itself.
  const STUCK_SECS = 60;
  const stuck = (r) => (r.word ? (now - r.at > STUCK_SECS ? 'settle' : null) : (now - r.at > (stats?.rd ?? 3600) ? 'retry' : null));
  const receive = (r) => act('Receive ORE', async () => {
    if (stuck(r) === 'retry') await send('Roll the dice again for your ORE', (s) => contract('Fleet', s).retry(r.id));
    else await send('Receive your ORE', (s) => contract('Fleet', s).settle(r.id));
  })();
  const upgrade = (sh) => act(`Upgrade #${sh.id}`, async () => {
    const c = await contract('Shipyard').upgradeCost(sh.level); checkOre(c);
    const approved = (await contract('ORE').allowance(account, ADDR.Shipyard)) >= c;
    if (!approved && !(await allowOre(c, 2))) return;
    await send(`${approved ? '' : 'Step 2 of 2: '}upgrade ship #${sh.id} for ${fmt(c, 0)} ORE`, (s) => contract('Shipyard', s).upgrade(sh.id));
  })();
  const repair = (sh) => act(`Repair #${sh.id}`, async () => {
    const [cpp, maxD] = await Promise.all([contract('Shipyard').repairCostPerPoint(), contract('Ships').maxDurability()]);
    const c = cpp * BigInt(Number(maxD) - sh.durability); checkOre(c);
    const approved = (await contract('ORE').allowance(account, ADDR.Shipyard)) >= c;
    if (!approved && !(await allowOre(c, 2))) return;
    await send(`${approved ? '' : 'Step 2 of 2: '}repair ship #${sh.id} for ${fmt(c, 0)} ORE`, (s) => contract('Shipyard', s).repair(sh.id));
  })();
  const doSell = act('Sell', async () => {
    const { ship, price } = sell;
    let wei; try { wei = ethers.parseEther(String(price).trim()); } catch (_) { throw new Error('Enter a price in BNB, for example 0.05.'); }
    if (wei <= 0n) throw new Error('Enter a price above 0.');
    setSell(null);
    const approved = await contract('Ships').isApprovedForAll(account, ADDR.Marketplace);
    if (!approved && !(await allowShips(ADDR.Marketplace, 2))) return;
    await send(`${approved ? '' : 'Step 2 of 2: '}put ship #${ship.id} up for sale at ${fmt(wei)} ${NET.currency}`, (s) => contract('Marketplace', s).list(ship.id, wei));
  });
  const cancelSale = (sh) => act(`Cancel sale #${sh.id}`, async () => { await send(`Take ship #${sh.id} off sale`, (s) => contract('Marketplace', s).cancel(sh.id)); })();
  const mintGen1 = act('Build Gen1 ship', async () => {
    const q = await contract('Shipyard').quoteFull(gen1.n);
    if (!q[0]) throw new Error(reasonText(q[1]));
    const maxOre = (q[2] * 103n) / 100n; // 3% slippage guard; the contract burns the exact price at mint time
    checkOre(q[2]);
    const approved = (await contract('ORE').allowance(account, ADDR.Shipyard)) >= maxOre;
    if (!approved && !(await allowOre(maxOre, 2))) return;
    await send(`${approved ? '' : 'Step 2 of 2: '}build ${gen1.n} Gen1 ship${gen1.n > 1 ? 's' : ''}`, (s) => contract('Shipyard', s).mintGen1(gen1.n, maxOre));
  });

  const stateLine = (sh) => {
    if (sh.where === 'wallet') return 'In your hangar';
    if (sh.where === 'listed') return 'For sale';
    if (locked(sh)) return 'Coming back with cargo…';
    return sh.kind === 'Pirate' ? 'Out raiding' : 'Out mining';
  };
  const mainAction = (sh) => {
    if (sh.where === 'wallet') return <button className="action-btn" disabled={busy} onClick={sendToWork([sh], `Send ship #${sh.id} to ${job(sh)}`)}>Send to {job(sh)}</button>;
    if (sh.where === 'listed') return <button className="action-btn" disabled={busy} onClick={() => cancelSale(sh)}>Cancel sale</button>;
    if (sh.kind === 'Pirate') return <button className="action-btn" disabled={busy} onClick={() => openCollect('Collect loot', [], [sh.id], false)}>Collect loot</button>;
    if (locked(sh)) return <button className="action-btn" disabled title="This ship is on its way back. See 'On the way' below.">On the way…</button>;
    const wait = readyIn(sh);
    if (wait > 0) return <button className="action-btn" disabled title="Miners need some time out before they can come back">Collect in {dur(wait)}</button>;
    return <button className="action-btn" disabled={busy} onClick={() => openCollect('Collect ORE', [sh.id], [], false)}>Collect ORE</button>;
  };
  // Secondary actions under the main button (replaces the old ⋯ menu). Costs come from Shipyard (upgradeCost = base × level, repair = per point).
  const extras = (sh) => {
    const items = [];
    const home = sh.where === 'wallet';
    if (sh.where === 'staked' && !locked(sh)) {
      if (sh.kind === 'Pirate') items.push({ label: 'Bring home', onClick: () => openCollect('Bring home', [], [sh.id], true), title: 'Collect its loot and bring it back to your hangar' });
      else items.push({ label: 'Bring home', onClick: () => (readyIn(sh) > 0 ? setStatus({ kind: 'err', text: `Ship #${sh.id} can come home in ${dur(readyIn(sh))}.` }) : openCollect('Bring home', [sh.id], [], true)), title: 'Collect its ORE and bring it back to your hangar' });
    }
    if (sh.kind === 'Miner' && home) { // upgrading needs the ship at home; hidden while it is out so the row stays short
      const max = stats && sh.level >= stats.maxLv;
      const cost = stats ? stats.upBase * BigInt(sh.level) : null;
      items.push({ label: 'Upgrade', disabled: max, onClick: () => upgrade(sh),
        title: max ? 'Max level' : `Level ${sh.level} → ${sh.level + 1}${cost !== null ? ` for ${fmt(cost, 0)} ORE` : ''}` });
    }
    if (sh.where !== 'listed') {
      const lost = stats ? stats.maxD - sh.durability : 10 - sh.durability;
      const cost = stats ? stats.cpp * BigInt(Math.max(0, lost)) : null;
      items.push({ label: 'Repair', disabled: lost <= 0, cls: sh.durability <= 3 ? 'sc-x-bad' : sh.durability <= 5 ? 'sc-x-warn' : '', onClick: () => repair(sh),
        title: lost <= 0 ? 'Condition is full' : `Back to full condition${cost !== null ? ` for ${fmt(cost, 0)} ORE` : ''}` });
      items.push({ label: 'Sell', disabled: !home, onClick: () => setSell({ ship: sh, price: '0.05' }), title: home ? 'Put it up for sale on the market' : 'Bring the ship home to sell' });
    }
    return items;
  };

  const staked = list.filter((s) => s.where === 'staked');
  const claimable = staked.reduce((t, s) => t + s.pending, 0n); // miners' cargo (before fuel + tax) + pirates' loot (no tax)
  const loot = staked.filter((s) => s.kind === 'Pirate').reduce((t, s) => t + s.pending, 0n);
  // What the Safe route actually pays, per ship, as Fleet/StealMath.split does it: fuel = max(cargo × fuelBps, fuelMin) capped at the cargo
  // (burned), then pirates take safeTaxBps of the rest. Pirate loot is paid as is.
  const safeNet = stats ? staked.filter((s) => s.kind === 'Miner').reduce((t, s) => {
    const r = s.pending; if (r === 0n) return t;
    let f = (r * BigInt(stats.fuelBps)) / 10000n; if (f < stats.fuelMin) f = stats.fuelMin; if (f > r) f = r;
    const rest = r - f; return t + rest - (rest * BigInt(stats.safeTaxBps)) / 10000n;
  }, loot) : null;
  const netTitle = stats ? `Safe route: ${stats.fuelBps / 100}% of each ship's cargo is burned as fuel (at least ${fmt(stats.fuelMin, 0)} ORE), then pirates take ${stats.safeTaxBps / 100}% of the rest.${loot > 0n ? ` Includes ${fmt(loot, 0)} ORE pirate loot (no tax).` : ''} Exact: ${fmt(safeNet, 0)} ORE.` : '';
  const minerGross = claimable - loot;
  const netRows = stats ? [['Ready (gross)', `${short(minerGross)} ORE`], ['Fuel burned', `${stats.fuelBps / 100}% · min ${fmt(stats.fuelMin, 0)}/ship`], ['Pirate tax', `${stats.safeTaxBps / 100}% of the rest`],
    ...(loot > 0n ? [['Pirate loot', `+${short(loot)} ORE`]] : []), ['You get ≈', `${short(safeNet)} ORE`]] : null;
  // Estimate for one more Common miner (Fleet.powerOf(Common, 1)) at today's rate; stakers only see it once ships loaded.
  const myPower = staked.filter((s) => s.kind === 'Miner').reduce((t, s) => t + BigInt(s.stake.weight), 0n);
  const g1hint = (() => {
    if (!account || !ships || !stats || !g1d || rateNow === null || rateNow === 0n) return null;
    const add = BigInt(g1d.powers[0]); const T = stats.tp; const p = myPower;
    if (T > 0n && p === T) return { full: true };
    const from = T > 0n ? Number((p * 10000n) / T) / 100 : 0; const to = Number(((p + add) * 10000n) / (T + add)) / 100;
    const plus = (rateNow * (p + add)) / (T + add) - (T > 0n ? (rateNow * p) / T : 0n);
    const f2 = (x) => (x >= 10 ? x.toFixed(1) : x.toFixed(2));
    return { from: f2(from), to: f2(to), plus };
  })();
  const openReqs = reqs.filter((r) => !r.settled);
  const sellUsd = sell && usdPerBnb && Number(sell.price) > 0 ? Number(sell.price) * usdPerBnb : null;

  return (
    <div className="dashboard-container" style={{ maxWidth: 1100 }}>
      <h1>Your Fleet {stats && <Info label="Game totals" testid="totals-info" rows={[['Fleet power', String(stats.tp)], ['Pirate rank weight', String(stats.rw)], ['Paid to miners', `${short(stats.paid)} ORE`], ['Fuel burned', `${short(stats.fuel)} ORE`]]} />}</h1>
      <SeasonPanel fleet={{
        power: staked.filter((s) => s.kind === 'Miner').reduce((t, s) => t + BigInt(s.stake.weight), 0n),
        ready: claimable, net: safeNet, netTitle, netRows, mined,
        note: readyMiners.length + stakedPirates.length === 0 && staked.some((s) => s.kind === 'Miner') ? `Miners can be collected ${dur(minStake)} after they start mining.` : null,
        actions: ships !== null && ships.length > 0 && (idleShips.length > 0 || staked.length > 0) ? <>
          {staked.length > 0 && <button className="action-btn" data-testid="collect-all" disabled={busy}
            onClick={() => openCollect('Collect all', readyMiners.map((s) => s.id), stakedPirates.map((s) => s.id), false)}>Collect all</button>}
          {idleShips.length > 0 && <button className="action-btn af-ghost" disabled={busy} onClick={sendToWork(idleShips, `Send ${idleShips.length} ship${idleShips.length > 1 ? 's' : ''} to work`)}>Send all to work ({idleShips.length})</button>}
        </> : null }} />

      {ships === null ? <p style={{ marginTop: '1rem' }}>Loading ships…</p> : ships.length === 0 ? <p style={{ marginTop: '1rem' }}>No ships yet — mint some first.</p> : (
        <div className="ship-grid" data-testid="ships">
          {list.map((sh) => (
              <ShipCard key={String(sh.id)} sh={sh} status={stateLine(sh)}
                sale={sh.where === 'listed' ? <>{fmt(sh.listing.price)}<span className="sp-unit"> {NET.currency}</span></> : null}
                action={mainAction(sh)}
                condInfo={<Info label="Condition">Every trip home costs the ship 1 condition point and burns a little ORE as fuel{stats ? ` (${stats.fuelBps / 100}% of the cargo, at least ${fmt(stats.fuelMin, 0)} ORE)` : ''}. Repair it with the Repair button.</Info>}
                extras={extras(sh)} />
          ))}
        </div>
      )}

      {openReqs.length > 0 && (
        <div className="card" style={{ marginTop: '1rem' }} data-testid="claims">
          <h3>On the way</h3>
          {openReqs.slice(0, 8).map((r) => (
            <p key={String(r.id)} style={{ marginTop: '.4rem' }}>{r.n} miner{r.n > 1 ? 's' : ''} {r.unstake ? 'coming home' : 'bringing cargo'} · {fmt(r.total, 0)} ORE · {r.route ? 'gamble' : 'safe'} · <span data-testid="claim-state">{stuck(r) ? 'taking longer than usual' : 'Bringing your ORE home…'}</span>
              {stuck(r) && <button title="Only needed if your ORE did not arrive by itself" className="action-btn" data-testid="claim-receive" style={{ marginLeft: '1rem', marginTop: 0 }} disabled={busy} onClick={() => receive(r)}>Receive ORE</button>}</p>
          ))}
        </div>
      )}

      <Gen1Panel d={g1d ? { ...g1d, quote: gen1, hint: g1hint } : null} n={gen1.n} setN={(n) => setGen1((g) => ({ ...g, n }))} ore={account ? ore : null} account={account}
        busy={busy} onBuild={mintGen1} now={now}>
        {g1pend.filter((p) => p.status !== 3).map((p) => (
          <p key={String(p.pid)} className="g1-pend">{p.count} ship{p.count > 1 ? 's' : ''} being built · {p.status === 1 ? 'rolling the dice…' : 'delivering…'}
            {p.status === 2 && <button title="Only needed if automatic delivery is stuck" className="action-btn" style={{ marginLeft: '1rem', marginTop: 0 }} disabled={busy} onClick={act('Deliver', async () => { await send('Deliver your new ships', (s) => contract('Shipyard', s).settleGen1(p.pid)); })}>Deliver now</button>}</p>
        ))}
      </Gen1Panel>
      {reveal && <Reveal {...reveal} account={account} onClose={() => setReveal(null)} />}

      {collect && (
        <Modal title={collect.title} testid="collect-modal" onClose={() => setCollect(null)}>
          <p className="cm-sub">{collect.miners.length} miner{collect.miners.length !== 1 ? 's' : ''}{collect.pirates.length ? ` and ${collect.pirates.length} pirate${collect.pirates.length > 1 ? 's' : ''}` : ''}. How should your miners bring the ORE home?</p>
          <button className={`cm-wallet${route === 0 ? ' af-on' : ''}`} data-testid="route-safe" onClick={() => setRoute(0)}>
            <span className="cm-ico">🛡️</span><span className="cm-name">Safe<br /><small style={{ color: 'var(--muted)' }}>Pirates take {stats?.safeTax ?? 30}%, you keep the rest.</small></span>{route === 0 && <span className="cm-tag">✓</span>}
          </button>
          <button className={`cm-wallet${route === 1 ? ' af-on' : ''}`} data-testid="route-gamble" onClick={() => setRoute(1)}>
            <span className="cm-ico">🎲</span><span className="cm-name">Gamble<br /><small style={{ color: 'var(--muted)' }}>Pirates take only {stats?.riskyTax ?? 15}%, but there is a 1 in {Math.round(100 / (stats?.intercept || 10))} chance they steal it all.</small></span>{route === 1 && <span className="cm-tag">✓</span>}
          </button>
          <p className="cm-note" style={{ color: 'var(--muted)' }}>A little ORE is burned as fuel and each ship loses 1 condition point. Your ORE arrives by itself a few seconds later.</p>
          <button className="action-btn" data-testid="collect-go" disabled={busy} onClick={doCollect}>{collect.unstake ? 'Bring home' : 'Collect'}</button>
        </Modal>
      )}

      {sell && (
        <Modal title={`Sell ship #${sell.ship.id}`} testid="sell-modal" onClose={() => setSell(null)}>
          <p className="cm-sub">{tierLabel(sell.ship)} {sell.ship.kind}. Buyers pay in {NET.currency}. A 5% market fee is taken when it sells; you can cancel any time before that.</p>
          <div className="elig-row">
            <input data-testid="sell-price" inputMode="decimal" value={sell.price} onChange={(e) => setSell({ ...sell, price: e.target.value })} aria-label={`Price in ${NET.currency}`} />
            <span className="price" style={{ alignSelf: 'center' }}>{NET.currency}</span>
          </div>
          <p className="cm-sub" data-testid="sell-usd" style={{ marginTop: '.5rem' }}>{sellUsd !== null ? `≈ $${sellUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '\u00a0'}</p>
          <button className="action-btn" data-testid="sell-go" disabled={busy} onClick={doSell}>Put up for sale</button>
        </Modal>
      )}
    </div>
  );
}

export default Dashboard;
